// src/utils/constants.ts

import { CategoryInfo, Occasion, SortOption } from '../types';

const _waNum = process.env.REACT_APP_WHATSAPP_NUMBER ?? '9056609810';
export const WHATSAPP_NUMBER  = `91${_waNum}`;        // wa.me format: 919056609810
export const WHATSAPP_DISPLAY = `+91 ${_waNum}`;      // display: +91 9056609810
export const WHATSAPP_PHONE   = `+91-${_waNum}`;      // schema/SEO: +91-9056609810

export const WHATSAPP_URL = (message = 'Hi! I am interested in your products.'): string =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

// Local image fallback — via.placeholder.com is a dead service
export const PLACEHOLDER_IMG: string =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">` +
    `<rect width="400" height="400" fill="#f5efe4"/>` +
    `<g fill="none" stroke="#a47c4a" stroke-width="2" opacity="0.5">` +
    `<rect x="140" y="130" width="120" height="120" rx="4"/>` +
    `<circle cx="175" cy="165" r="12"/>` +
    `<path d="M140 225 L180 190 L210 215 L235 195 L260 218"/>` +
    `</g>` +
    `<text x="200" y="290" font-family="Jost,sans-serif" font-size="14" fill="#a47c4a" text-anchor="middle" opacity="0.7">Image unavailable</text>` +
    `</svg>`
  );

export const CATEGORIES: CategoryInfo[] = [
  { name: 'Kurta',             emoji: '👘', desc: 'Elegant daily wear'   },
  { name: 'Suits',             emoji: '🥻', desc: 'Salwar & Kameez'      },
  { name: 'Palazzo Sets',      emoji: '👗', desc: 'Flowy & stylish'      },
  { name: 'Velvet Collection', emoji: '✨', desc: 'Premium velvet'       },
  { name: 'Woolen',            emoji: '🧶', desc: 'Winter warmth'        },
  { name: 'Coord Sets',        emoji: '💃', desc: 'Trendy co-ords'       },
];

// Font Awesome (free, solid) icon per category — used by the Shop by
// Category grid and the Category Bubbles row instead of emoji, since the
// live `categories` admin table doesn't have per-category icons/emoji filled
// in for every name yet. Falls back to a generic bag icon for any category
// name not listed here (new categories added later, etc).
const CATEGORY_ICONS: Record<string, string> = {
  'Kurta':             'fa-shirt',
  'Suits':             'fa-vest',
  'Palazzo Sets':      'fa-person-dress',
  'Velvet Collection': 'fa-gem',
  'Woolen':            'fa-mitten',
  'Coord Sets':        'fa-vest-patches',
  'Tops':              'fa-shirt',
  'Bottoms':           'fa-socks',
  'Dresses':           'fa-person-dress',
  'Frock Suit':        'fa-child-dress',
  'Others':            'fa-bag-shopping',
};
const DEFAULT_CATEGORY_ICON = 'fa-bag-shopping';

export const getCategoryIcon = (name: string): string =>
  CATEGORY_ICONS[name] ?? DEFAULT_CATEGORY_ICON;

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'newest',     label: 'Newest First'        },
  { value: 'price_asc',  label: 'Price: Low to High'  },
  { value: 'price_desc', label: 'Price: High to Low'  },
];

export const ALL_CATEGORIES: string[] = [
  'All', 'Kurta', 'Suits', 'Palazzo Sets', 'Coord Sets',
  'Velvet Collection', 'Woolen', 'Dresses', 'Bottoms', 'Others',
];

// Category name <-> URL slug (e.g. 'Palazzo Sets' <-> 'palazzo-sets') —
// used for /products/:category path routes instead of ?category= query
// params, so category pages are real, distinct, crawlable/prerenderable URLs.
export const slugifyCategory = (name: string): string =>
  name.toLowerCase().replace(/\s+/g, '-');

export const categoryFromSlug = (slug: string): string | undefined =>
  ALL_CATEGORIES.find((c) => slugifyCategory(c) === slug.toLowerCase());

export const OCCASIONS: Occasion[] = [
  'Casual', 'Office', 'Festive', 'Wedding', 'Party',
];

export const CHAT_SUGGESTIONS: string[] = [
  'Show kurtas under ₹1000',
  'Size guide',
  'WhatsApp enquiry',
  'New arrivals',
];

export const AI_SEARCH_SUGGESTIONS: string[] = [
  'Red velvet kurta for wedding',
  'Palazzo set under ₹1200',
  'Warm woolen suit for winter',
  'Cotton kurti for daily wear',
  'Ethnic coord set for party',
];

export const PRICE_PRESETS: { label: string; min: string; max: string }[] = [
  { label: 'Under ₹800',  min: '',    max: '800'  },
  { label: '₹800–1500',   min: '800', max: '1500' },
  { label: '₹1500+',      min: '1500',max: ''     },
];

// Home page "Shop by Occasion" lifestyle cards — static curation, not tied to
// a specific category. gradientFrom/To drive the card's own SVG background.
export interface OccasionCard {
  eyebrow:      string;
  title:        string;
  icon:         string; // Font Awesome (free, solid) class
  gradientFrom: string;
  gradientTo:   string;
  category:     string; // links into the category filter closest to the vibe
}

export const OCCASION_SHOWCASE: OccasionCard[] = [
  { eyebrow: 'Diwali & Beyond', title: 'Festive Wear',      icon: 'fa-fire',               gradientFrom: '#3a1f1a', gradientTo: '#a47c4a', category: 'Velvet Collection' },
  { eyebrow: 'Big Day Ready',   title: 'Wedding Season',    icon: 'fa-ring',               gradientFrom: '#5c2b1f', gradientTo: '#f0ddb8', category: 'Suits' },
  { eyebrow: 'Comfort First',   title: 'Everyday Elegance', icon: 'fa-mug-hot',            gradientFrom: '#1a3a2e', gradientTo: '#7a5a30', category: 'Kurta' },
  { eyebrow: 'Dance the Night', title: 'Party Ready',       icon: 'fa-champagne-glasses',  gradientFrom: '#2a1a3a', gradientTo: '#f7c381', category: 'Coord Sets' },
];

// Hero slider — curated spotlight slides. Images point at real product photos
// already on the live uploads server (accessible locally too, since local
// env points UPLOADS_URL at production — see admin/backend-node/env/.env).
// Swap `image` for Phot.AI-generated art once supplied; everything else
// (copy, tag) stays the same shape.
export interface HeroSlide {
  badge:    string;
  title:    string;
  em:       string;
  subtitle: string;
  image:    string;
  tag?:     string;
}

const HERO_IMG_BASE = 'https://api.balajifashionhouse.com/uploads/products';

export const HERO_SLIDES: HeroSlide[] = [
  {
    badge: '🇮🇳 Indian Ethnic Wear',
    title: 'Elegance in Every',
    em: 'Thread & Weave',
    subtitle: 'Discover our exquisite collection of kurtas, suits, palazzo sets & more — crafted for the modern Indian woman.',
    image: `${HERO_IMG_BASE}/product_ghost_1785673906863.webp`,
  },
  {
    badge: '❄️ Winter Edit',
    title: 'Wrapped in Warmth,',
    em: 'Woven with Grace',
    subtitle: 'Rich woolen kurta sets and layered silhouettes made for the season ahead.',
    image: `${HERO_IMG_BASE}/product_ghost_1785674000838.webp`,
    tag: 'Winter Collection',
  },
  {
    badge: '✨ Just Landed',
    title: 'Fresh Prints,',
    em: 'New Beginnings',
    subtitle: 'The latest arrivals in vibrant palazzo sets and everyday ethnic wear.',
    image: `${HERO_IMG_BASE}/product_ghost_1785674534302.webp`,
    tag: 'New In',
  },
  {
    badge: '🎉 Festive Ready',
    title: 'Celebrate in',
    em: 'Timeless Style',
    subtitle: 'Statement pieces for weddings, festivals and every special occasion.',
    image: `${HERO_IMG_BASE}/product_ghost_1785674610985.webp`,
    tag: 'Festive Edit',
  },
];
